<script>
  import { onMount } from "svelte";
  import {
    configStore,
    startUpload,
    verifyEmail as apiVerifyEmail,
    checkRememberToken,
    getRememberSettings,
    isReportableUploadError,
    reportUploadClientError,
    uploadFilesApi,
    submitUpload as apiSubmitUpload,
  } from "../api/index.js";
  import {
    Button,
    Card,
    Input,
    Select,
    Checkbox,
    Textarea,
    ProgressSteps,
    FileDropzone,
    Modal,
    Switch,
    Icon,
  } from "./ui/index.js";
  import Alert from "./ui/Alert.svelte";
  import { toastStore } from "../stores/toast.js";

  /** @type {{ onStepChange?: (step: number) => void, ref?: { reset: () => void, prevStep?: () => void, nextStep?: () => void, currentStep: number, isLoading?: boolean }, isLicensed?: boolean }} */
  let { onStepChange, ref = $bindable(), isLicensed = true } = $props();

  // Config state
  let config = $state({
    loaded: false,
    loading: false,
    error: null,
    branding: null,
    features: null,
    form: null,
  });

  // Remember verification settings
  let rememberSettings = $state({ enabled: false, durationHours: 0 });

  // Upload flow state
  let currentStep = $state(1);
  let email = $state("");
  let verificationCode = $state("");
  let emailVerified = $state(false);
  let sessionToken = $state("");
  let isLoading = $state(false);
  let needsVerificationCode = $state(false); // Show code input after sending
  let trustThisBrowser = $state(false); // User opt-in to remember verification

  // Dynamic form data - will be populated based on config fields
  let formData = $state({});
  let selectedFiles = $state([]);

  // Field memory - saved form data from previous uploads
  let savedFormData = $state(null);
  let showConfirmationDialog = $state(false);
  let fieldConfirmations = $state({});

  // Submission result
  let submissionId = $state("");
  let downloadToken = $state("");

  // Load config on mount
  onMount(() => {
    const unsubscribe = configStore.subscribe((value) => {
      config = value;
      // Initialize formData when config loads
      if (value.form?.fields) {
        const initialData = {};
        value.form.fields.forEach((field) => {
          if (field.type === "checkbox") {
            initialData[field.name] = false;
          } else {
            initialData[field.name] = "";
          }
        });
        formData = initialData;
      }
    });

    if (!config.loaded && !config.loading) {
      configStore.load().catch((err) => {
        console.error("Failed to load config:", err);
      });
    }

    // Load remember settings
    loadRememberSettings();

    return unsubscribe;
  });

  async function loadRememberSettings() {
    try {
      const settings = await getRememberSettings();
      console.log("[UploadWizard] Loaded remember settings:", settings);
      rememberSettings = settings;
    } catch (err) {
      console.error("Failed to load remember settings:", err);
    }
  }

  // Email validation helper
  function isValidEmail(emailStr) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
  }

  async function reportClientUploadError(step, operation, err, metadata = {}) {
    if (!isReportableUploadError(err)) {
      return;
    }

    await reportUploadClientError({
      step,
      operation,
      error: err,
      sessionToken,
      email,
      metadata,
    });
  }

  // Reset verification state to start over
  function resetVerification() {
    email = "";
    verificationCode = "";
    sessionToken = "";
    needsVerificationCode = false;
    emailVerified = false;
    trustThisBrowser = false;
  }

  // "Continue" button - checks for remembered token, then sends code if needed
  async function handleContinueEmail() {
    if (!isLicensed) {
      toastStore.error(
        "Service unavailable: Invalid or expired license. Please contact support."
      );
      return;
    }

    if (!email || !isValidEmail(email)) {
      toastStore.error("Please enter a valid email address.");
      return;
    }

    isLoading = true;

    const normalizedEmail = email.toLowerCase();

    // Always check for a saved remember token in localStorage
    // The backend will tell us if remember verification is enabled
    const savedToken = getRememberToken(normalizedEmail);
    console.log(
      "[UploadWizard] Checking remember token for:",
      normalizedEmail,
      "token exists:",
      !!savedToken
    );

    if (savedToken) {
      try {
        console.log("[UploadWizard] Validating remember token with backend...");
        const result = await checkRememberToken(normalizedEmail, savedToken);
        console.log("[UploadWizard] Token validation result:", result);
        if (result.valid && result.sessionToken) {
          sessionToken = result.sessionToken;
          emailVerified = true;
          toastStore.success("Welcome back! Your email is already verified.");
          checkForSavedData();
          isLoading = false;
          currentStep = 2;
          return;
        } else {
          // Token not valid, remove it
          console.log(
            "[UploadWizard] Token invalid, removing from localStorage"
          );
          removeRememberToken(normalizedEmail);
        }
      } catch (err) {
        // Token invalid or expired, remove it and continue to send code
        console.log("[UploadWizard] Token check failed:", err);
        removeRememberToken(normalizedEmail);
      }
    }

    // No valid remember token, send verification code
    try {
      const data = await startUpload(email);
      sessionToken = data.sessionToken;
      needsVerificationCode = true;
      if (data.emailDisabled) {
        toastStore.warning(
          "Email is not configured. Check server console for verification code."
        );
      } else {
        toastStore.success("Verification code sent! Check your email.");
      }
    } catch (err) {
      await reportClientUploadError("email", "start", err);
      toastStore.error("Network error. Please try again.");
    } finally {
      isLoading = false;
    }
  }

  // LocalStorage helpers for remember tokens
  function getRememberToken(userEmail) {
    try {
      const token = localStorage.getItem(`pd_remember_${userEmail}`);
      if (!token) return null;

      // Check if token has expired client-side
      const expiresAt = localStorage.getItem(`pd_remember_exp_${userEmail}`);
      if (expiresAt && Date.now() > parseInt(expiresAt)) {
        // Token expired, clean up
        removeRememberToken(userEmail);
        return null;
      }

      return token;
    } catch {
      return null;
    }
  }

  function saveRememberToken(userEmail, token, expiresIn) {
    try {
      console.log(
        "[UploadWizard] Saving remember token for:",
        userEmail,
        "expires in:",
        expiresIn,
        "seconds"
      );
      localStorage.setItem(`pd_remember_${userEmail}`, token);
      // Also save expiry time
      const expiresAt = Date.now() + expiresIn * 1000;
      localStorage.setItem(
        `pd_remember_exp_${userEmail}`,
        expiresAt.toString()
      );
    } catch {
      // localStorage not available
    }
  }

  function removeRememberToken(userEmail) {
    try {
      localStorage.removeItem(`pd_remember_${userEmail}`);
      localStorage.removeItem(`pd_remember_exp_${userEmail}`);
    } catch {
      // localStorage not available
    }
  }

  // Derived: form fields from config
  let formFields = $derived(config.form?.fields || []);

  // Derived: visible form fields based on conditional logic
  // Note: We spread formData to ensure Svelte tracks all property changes
  let visibleFormFields = $derived.by(() => {
    const fields = config.form?.fields || [];
    // Access formData as a whole to ensure reactivity tracking
    /** @type {Record<string, any>} */
    const currentFormData = { ...formData };

    console.log(
      "[Conditional] Evaluating visibility, providerType =",
      currentFormData.providerType
    );

    // Debug: log all fields and their conditional config
    fields.forEach((f) => {
      if (f.name === "blahBlah") {
        console.log(
          "[Conditional] blahBlah field conditional config:",
          JSON.stringify(f.conditional)
        );
      }
    });

    return fields.filter((field) => {
      // Debug: log every field's conditional check
      console.log(
        `[Conditional] Checking field "${field.name}", conditional:`,
        field.conditional
      );

      // If no conditional logic, field is always visible
      if (!field.conditional?.enabled) {
        console.log(
          `[Conditional] Field "${field.name}" has no conditional or not enabled`
        );
        return true;
      }
      if (
        !field.conditional.conditions ||
        field.conditional.conditions.length === 0
      ) {
        console.log(`[Conditional] Field "${field.name}" has no conditions`);
        return true;
      }

      const logic = field.conditional.logic || "AND";
      const results = field.conditional.conditions.map((cond) => {
        const fieldValue = currentFormData[cond.field] ?? "";
        const result = (() => {
          switch (cond.operator) {
            case "equals":
              return fieldValue === cond.value;
            case "notEquals":
              return fieldValue !== cond.value;
            case "contains":
              return String(fieldValue)
                .toLowerCase()
                .includes(String(cond.value).toLowerCase());
            case "notContains":
              return !String(fieldValue)
                .toLowerCase()
                .includes(String(cond.value).toLowerCase());
            case "isEmpty":
              return !fieldValue || fieldValue === "";
            case "isNotEmpty":
              return fieldValue && fieldValue !== "";
            default:
              return true;
          }
        })();
        console.log(
          `[Conditional] Field "${field.name}": ${cond.field} ${cond.operator} "${cond.value}" => "${fieldValue}" = ${result}`
        );
        return result;
      });

      const visible =
        logic === "AND" ? results.every((r) => r) : results.some((r) => r);
      console.log(`[Conditional] Field "${field.name}" visible: ${visible}`);
      return visible;
    });
  });

  // Debug: Log when visibleFormFields changes
  $effect(() => {
    console.log(
      "[Conditional] Visible fields:",
      visibleFormFields.map((f) => f.name)
    );
  });

  // Derived: fields that require reconfirmation (only visible ones with requireConfirmation flag)
  let fieldsRequiringConfirmation = $derived(
    visibleFormFields.filter((f) => f.remember && f.requireConfirmation)
  );

  // Count of unconfirmed fields
  let unconfirmedCount = $derived(() => {
    if (!savedFormData) return 0;
    return fieldsRequiringConfirmation.filter(
      (f) => !fieldConfirmations[f.name]
    ).length;
  });

  // All fields confirmed?
  let allFieldsConfirmed = $derived(() => {
    return unconfirmedCount() === 0;
  });

  // Derived: branding info
  let companyName = $derived(
    config.branding?.companyName || config.branding?.appName || "Provider Drop"
  );
  let logoUrl = $derived(config.branding?.logoUrl || "");
  let brandingPhone = $derived(config.branding?.phone || "");
  let brandingEmail = $derived(config.branding?.email || "");

  // Logo display options (match landing page)
  let logoMaxWidth = $derived(config.branding?.logoMaxWidth || 400);
  let logoMaxHeight = $derived(config.branding?.logoMaxHeight || 100);
  let logoBackground = $derived(config.branding?.logoBackground || "none");
  let logoContainerStyle = $derived(
    config.branding?.logoContainerStyle || "none"
  );
  let hideCompanyName = $derived(config.branding?.hideCompanyName || false);

  // Derived: max file size
  let maxFileSizeMB = $derived(config.features?.maxFileSizeMB || 10);

  // Derived: allowed file types (convert extensions to dot-prefixed format)
  let allowedFileTypes = $derived(
    (
      config.features?.allowedTypes || [
        "pdf",
        "doc",
        "docx",
        "txt",
        "jpg",
        "jpeg",
        "png",
        "gif",
      ]
    ).map((ext) => (ext.startsWith(".") ? ext : `.${ext}`))
  );

  const steps = [
    { number: 1, title: "Email" },
    { number: 2, title: "Details" },
    { number: 3, title: "Files" },
    { number: 4, title: "Review" },
  ];

  async function handleVerifyEmail() {
    if (!isLicensed) {
      toastStore.error(
        "Service unavailable: Invalid or expired license. Please contact support."
      );
      return;
    }

    if (!verificationCode) return;

    isLoading = true;

    try {
      const result = await apiVerifyEmail(sessionToken, verificationCode);
      console.log("[UploadWizard] Verify email result:", result);
      if (result.verified) {
        emailVerified = true;

        // Save remember token only if user opted in to trust this browser
        if (
          trustThisBrowser &&
          result.rememberToken &&
          result.rememberExpiresIn
        ) {
          console.log(
            "[UploadWizard] User opted in to trust browser, saving remember token..."
          );
          saveRememberToken(
            email.toLowerCase(),
            result.rememberToken,
            result.rememberExpiresIn
          );
        } else if (!trustThisBrowser) {
          console.log(
            "[UploadWizard] User did not opt in to trust browser, not saving token"
          );
        } else {
          console.log(
            "[UploadWizard] No remember token in response (feature may be disabled)"
          );
        }

        toastStore.success("Email verified successfully!");
        checkForSavedData();
        currentStep = 2;
      } else {
        toastStore.error(result.message || "Invalid verification code");
      }
    } catch (err) {
      await reportClientUploadError("email", "verify", err);
      toastStore.error(err.message || "Verification failed");
      console.error("Verify email error:", err);
    } finally {
      isLoading = false;
    }
  }

  function checkForSavedData() {
    // Check localStorage for saved form data for this email
    try {
      const saved = localStorage.getItem(`formData_${email.toLowerCase()}`);
      if (saved) {
        const parsedData = JSON.parse(saved);
        savedFormData = parsedData;
        // Pre-fill form with saved data
        formData = { ...formData, ...parsedData };
        fieldConfirmations = {};
      }
    } catch (e) {
      console.error("Error loading saved form data:", e);
    }
  }

  function confirmField(fieldName) {
    fieldConfirmations = { ...fieldConfirmations, [fieldName]: true };
  }

  function handleConfirmationContinue() {
    showConfirmationDialog = false;
    currentStep = 3;
  }

  function handleConfirmationGoBack() {
    showConfirmationDialog = false;
  }

  function validateVisibleFields() {
    // Only validate visible required fields (skip info fields as they don't have user input)
    for (const field of visibleFormFields) {
      if (field.type === "info") {
        continue; // Info fields are display-only
      }
      if (field.required) {
        const value = formData[field.name];
        if (field.type === "checkbox") {
          if (!value) {
            toastStore.error(`${field.label} is required`);
            return false;
          }
        } else {
          if (!value || value.trim() === "") {
            toastStore.error(`${field.label} is required`);
            return false;
          }
        }
      }
    }
    return true;
  }

  function nextStep() {
    if (currentStep < 4) {
      // When leaving Step 2, validate visible required fields first
      if (currentStep === 2) {
        if (!validateVisibleFields()) {
          return;
        }
        // Then check if we need to confirm saved fields
        if (savedFormData && fieldsRequiringConfirmation.length > 0) {
          fieldConfirmations = {};
          showConfirmationDialog = true;
          return;
        }
      }
      currentStep++;
    }
  }

  function prevStep() {
    if (currentStep > 1) {
      // If going back to step 1, reset the verification UI state
      // (but keep the email so user doesn't have to re-enter)
      if (currentStep === 2) {
        // User already verified, just going back - reset for fresh start
        verificationCode = "";
        needsVerificationCode = false;
        emailVerified = false;
        sessionToken = "";
        // Keep trustThisBrowser as user may have checked it
      }
      currentStep--;
    }
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  async function handleSubmitUpload() {
    isLoading = true;
    let operation = "submit";

    try {
      // Save form data for next time (fields marked as "remember", excluding info fields)
      const dataToSave = {};
      formFields.forEach((f) => {
        if (f.type !== "info" && f.remember && formData[f.name]) {
          dataToSave[f.name] = formData[f.name];
        }
      });
      if (Object.keys(dataToSave).length > 0) {
        localStorage.setItem(
          `formData_${email.toLowerCase()}`,
          JSON.stringify(dataToSave)
        );
      }

      // Step 1: Upload the actual files
      if (selectedFiles.length > 0) {
        operation = "file-upload";
        toastStore.info("Uploading files...", "Please wait");
        const uploadResult = await uploadFilesApi(sessionToken, selectedFiles);
        if (!uploadResult.files || uploadResult.files.length === 0) {
          throw new Error("Failed to upload files");
        }
      }

      // Step 2: Submit the form data to complete the submission (exclude info fields)
      const submissionData = { email };
      formFields.forEach((f) => {
        if (f.type !== "info" && formData[f.name] !== undefined) {
          submissionData[f.name] = formData[f.name];
        }
      });
      submissionData.files = selectedFiles.map((f) => ({
        name: f.name,
        size: f.size,
      }));

      operation = "finalize";
      const result = await apiSubmitUpload(sessionToken, submissionData);

      submissionId = result.submissionId || "SUB-" + Date.now();
      downloadToken = result.downloadToken || "";
      toastStore.success("Upload completed successfully!", "Success");
      currentStep = 4;
    } catch (err) {
      await reportClientUploadError("upload", operation, err, {
        selectedFileCount: selectedFiles.length,
      });
      toastStore.error(err.message || "Failed to submit upload");
      console.error("Submit upload error:", err);
    } finally {
      isLoading = false;
    }
  }

  // Get field value for display
  function getFieldDisplayValue(fieldName) {
    return formData[fieldName] || savedFormData?.[fieldName] || "—";
  }

  // Reset the wizard to initial state
  function resetWizard() {
    currentStep = 1;
    email = "";
    verificationCode = "";
    emailVerified = false;
    sessionToken = "";
    needsVerificationCode = false;
    trustThisBrowser = false;

    // Reset formData based on config fields
    const initialData = {};
    formFields.forEach((field) => {
      if (field.type === "checkbox") {
        initialData[field.name] = false;
      } else {
        initialData[field.name] = "";
      }
    });
    formData = initialData;

    selectedFiles = [];
    savedFormData = null;
    showConfirmationDialog = false;
    fieldConfirmations = {};
    submissionId = "";
    downloadToken = "";
  }

  // Expose ref methods
  $effect(() => {
    ref = {
      reset: resetWizard,
      prevStep,
      nextStep,
      get currentStep() {
        return currentStep;
      },
      get isLoading() {
        return isLoading;
      },
    };
  });

  // Notify parent of step changes
  $effect(() => {
    onStepChange?.(currentStep);
  });

  // Warn user about losing changes when navigating away (steps 2-3 only)
  $effect(() => {
    function handleBeforeUnload(event) {
      if (currentStep > 1 && currentStep < 4) {
        event.preventDefault();
        event.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return event.returnValue;
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  });

  // Generate system filename based on naming pattern
  function generateSystemFilename(originalFilename, timestamp) {
    const lastDot = originalFilename.lastIndexOf(".");
    const filename =
      lastDot > 0 ? originalFilename.substring(0, lastDot) : originalFilename;
    const extension =
      lastDot > 0 ? originalFilename.substring(lastDot + 1) : "";

    // Simple naming pattern
    return `${email}_${timestamp}_${filename}.${extension}`;
  }

  // Format current date/time
  function formatDateTime() {
    const now = new Date();
    return now.toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  }

  // Download PDF summary
  function downloadPDF() {
    if (!submissionId || !downloadToken) {
      toastStore.error("Unable to download PDF summary. Please try again.");
      return;
    }

    // Create download URL with query parameters
    const downloadUrl = `/api/upload/summary?id=${encodeURIComponent(submissionId)}&token=${encodeURIComponent(downloadToken)}`;

    // Create a temporary link and trigger download
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `upload-summary-${submissionId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Render dynamic form field
  function renderField(field) {
    return field;
  }
</script>

<!-- Confirmation Dialog for Saved Fields -->
<Modal
  bind:open={showConfirmationDialog}
  title="Confirm Your Information"
  closeOnBackdrop={false}
  closeOnEscape={false}
>
  <p class="dialog-intro">
    We noticed you have saved information from a previous upload. Please confirm
    the following details are still correct:
  </p>

  <div class="confirm-fields">
    {#each fieldsRequiringConfirmation as field}
      <div class="confirm-field">
        <div class="confirm-field-header">
          <h4 class="confirm-field-label">{field.label}</h4>
          <label class="confirm-checkbox-label">
            <input
              type="checkbox"
              class="confirm-checkbox"
              checked={fieldConfirmations[field.name] || false}
              onchange={() => confirmField(field.name)}
            />
            <span class="confirm-checkbox-text">Confirm</span>
          </label>
        </div>
        <p class="confirm-field-value">
          {getFieldDisplayValue(field.name)}
        </p>
      </div>
    {/each}
  </div>

  <p class="dialog-help">
    Please review each field and click "Confirm" next to each one to verify the
    information is still correct. You can click "Go Back" to modify any values,
    or "Continue" once all fields are confirmed.
  </p>

  {#snippet actions()}
    <Button variant="outline" onclick={handleConfirmationGoBack}>
      Go Back
    </Button>
    <Button
      variant="primary"
      disabled={!allFieldsConfirmed()}
      onclick={handleConfirmationContinue}
    >
      {#if allFieldsConfirmed()}
        Continue
      {:else}
        Confirm {unconfirmedCount()} more field{unconfirmedCount() === 1
          ? ""
          : "s"}
      {/if}
    </Button>
  {/snippet}
</Modal>

<div class="wizard-page">
  <!-- Header -->
  <header class="wizard-header">
    <div class="header-container">
      <div class="header-branding">
        {#if logoUrl}
          {#if logoContainerStyle === "none"}
            <!-- No container - just the logo -->
            <img
              src={logoUrl}
              alt="{companyName} logo"
              class="header-logo-bare"
              style="max-width: {logoMaxWidth > 0
                ? logoMaxWidth + 'px'
                : '200px'}; max-height: {logoMaxHeight > 0
                ? logoMaxHeight + 'px'
                : '60px'};"
            />
          {:else}
            <!-- Logo with container -->
            <div
              class="header-logo-container"
              class:container-card={logoContainerStyle === "card"}
              class:container-border={logoContainerStyle === "border"}
              style="background: {logoBackground && logoBackground !== 'none'
                ? logoBackground
                : '#f8f9fa'};"
            >
              <img
                src={logoUrl}
                alt="{companyName} logo"
                class="header-logo"
                style="max-width: {logoMaxWidth > 0
                  ? logoMaxWidth + 'px'
                  : '200px'}; max-height: {logoMaxHeight > 0
                  ? logoMaxHeight + 'px'
                  : '60px'};"
              />
            </div>
          {/if}
        {/if}
        {#if !hideCompanyName}
          <div class="header-text">
            <h1>{companyName}</h1>
            <p>Secure File Upload</p>
          </div>
        {/if}
      </div>
    </div>

    <!-- Progress Steps - inside header for gradient background -->
    <div class="header-steps">
      <ProgressSteps {steps} {currentStep} />
    </div>
  </header>

  <!-- Wizard Content -->
  <div class="wizard-content">
    <Card variant="default" padding="lg">
      <!-- Step 1: Email Verification -->
      {#if currentStep === 1}
        <div class="step-content">
          <h2 class="step-title">Verify Your Email</h2>
          <p class="step-desc">
            Enter your email to continue. Returning users will be recognized
            automatically.
          </p>

          <div class="form-stack">
            <div class="email-input-row">
              <div class="email-input-wrapper">
                <Input
                  id="email"
                  type="email"
                  label="Email Address"
                  placeholder="Enter your email"
                  bind:value={email}
                  required
                  disabled={isLoading || needsVerificationCode}
                />
              </div>
              {#if needsVerificationCode}
                <button
                  class="start-over-btn"
                  onclick={resetVerification}
                  title="Start over with a different email"
                  disabled={isLoading}
                >
                  ↺
                </button>
              {/if}
            </div>

            {#if !needsVerificationCode}
              <!-- Initial state: just email and Continue button -->
              <Button
                fullWidth
                onclick={handleContinueEmail}
                disabled={isLoading || !email}
              >
                {isLoading ? "Checking..." : "Continue"}
              </Button>
            {:else}
              <!-- Code has been sent, show verification input -->
              <div class="divider">
                <span>Enter code</span>
              </div>

              <Input
                id="code"
                type="text"
                label="Verification Code"
                placeholder="Enter 6-digit code"
                bind:value={verificationCode}
                disabled={isLoading}
              />

              {#if rememberSettings.enabled}
                <div class="trust-browser-option">
                  <Switch
                    id="trust-browser"
                    bind:checked={trustThisBrowser}
                    disabled={isLoading}
                  />
                  <label for="trust-browser" class="trust-browser-label">
                    Trust this browser
                    <span class="trust-browser-hint">
                      (skip verification for {rememberSettings.durationHours >=
                      24
                        ? `${Math.floor(rememberSettings.durationHours / 24)} day${Math.floor(rememberSettings.durationHours / 24) !== 1 ? "s" : ""}`
                        : `${rememberSettings.durationHours} hour${rememberSettings.durationHours !== 1 ? "s" : ""}`})
                    </span>
                  </label>
                </div>
              {/if}

              <Button
                fullWidth
                onclick={handleVerifyEmail}
                disabled={isLoading || !verificationCode}
              >
                {isLoading ? "Verifying..." : "Verify Email"}
              </Button>

              <button
                class="resend-link"
                onclick={handleContinueEmail}
                disabled={isLoading}
              >
                Resend verification code
              </button>
            {/if}
          </div>
        </div>

        <!-- Step 2: Dynamic Form Fields -->
      {:else if currentStep === 2}
        <div class="step-content">
          <h2 class="step-title">Your Information</h2>
          <p class="step-desc">Please fill in the required details.</p>

          <div class="form-stack">
            {#each visibleFormFields as field (field.name)}
              {#if field.type === "info"}
                <Alert
                  type={field.infoSeverity || "info"}
                  title={field.infoTitle || ""}
                  message={field.infoMessage || field.label}
                  icon={field.icon || ""}
                />
              {:else if field.type === "text" || field.type === "email" || field.type === "tel"}
                <Input
                  id={field.name}
                  type={field.type}
                  label={field.label}
                  placeholder={field.placeholder ||
                    `Enter ${field.label.toLowerCase()}`}
                  helpText={field.helpText}
                  icon={field.icon || ""}
                  bind:value={formData[field.name]}
                  required={field.required}
                />
              {:else if field.type === "select"}
                <Select
                  id={field.name}
                  label={field.label}
                  placeholder={field.placeholder ||
                    `Select ${field.label.toLowerCase()}`}
                  options={field.options || []}
                  helpText={field.helpText}
                  icon={field.icon || ""}
                  bind:value={formData[field.name]}
                  required={field.required}
                />
              {:else if field.type === "checkbox"}
                <Checkbox
                  id={field.name}
                  label={field.helpText || field.label}
                  icon={field.icon || ""}
                  bind:checked={formData[field.name]}
                  required={field.required}
                />
              {:else if field.type === "textarea"}
                <Textarea
                  id={field.name}
                  label={field.label}
                  placeholder={field.placeholder ||
                    `Enter ${field.label.toLowerCase()}`}
                  helpText={field.helpText}
                  icon={field.icon || ""}
                  bind:value={formData[field.name]}
                  rows={3}
                />
              {/if}
            {/each}
          </div>
        </div>

        <!-- Step 3: File Upload -->
      {:else if currentStep === 3}
        <div class="step-content">
          <h2 class="step-title">Upload Files</h2>
          <p class="step-desc">
            Select or drag files to upload. Max {maxFileSizeMB}MB per file.
          </p>

          <FileDropzone
            bind:files={selectedFiles}
            maxSize={maxFileSizeMB * 1024 * 1024}
            accept={allowedFileTypes}
          />

          {#if selectedFiles.length > 0}
            <div class="submit-section">
              <Button
                variant="primary"
                fullWidth
                onclick={handleSubmitUpload}
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Submit Upload"}
              </Button>
            </div>
          {/if}
        </div>

        <!-- Step 4: Summary -->
      {:else if currentStep === 4}
        <div class="step-content">
          <!-- Summary Header -->
          <div class="summary-header">
            <div class="summary-check">
              <span class="check-icon">✓</span>
            </div>
            <h2 class="step-title">Upload Complete!</h2>
          </div>

          <!-- Printable Summary Card -->
          <div class="print-summary">
            <!-- Organization Branding -->
            <div class="summary-branding">
              <h3 class="org-name">{companyName}</h3>
              {#if brandingPhone || brandingEmail}
                <p class="org-contact">
                  {#if brandingPhone}Phone: {brandingPhone}{/if}
                  {#if brandingPhone && brandingEmail}
                    •
                  {/if}
                  {#if brandingEmail}Email: {brandingEmail}{/if}
                </p>
              {/if}
            </div>

            <div class="summary-title-section">
              <h2 class="summary-main-title">Upload Summary</h2>
              <p class="summary-date">Generated on {formatDateTime()}</p>
              {#if submissionId}
                <p class="submission-id">Reference: {submissionId}</p>
              {/if}
            </div>

            <!-- Contact Information -->
            <div class="summary-section">
              <h3 class="section-title">Contact Information</h3>
              <div class="section-content">
                <p><strong>Email:</strong> {email}</p>
              </div>
            </div>

            <!-- Form Data -->
            <div class="summary-section">
              <h3 class="section-title">Submission Details</h3>
              <div class="section-content">
                {#each visibleFormFields as field}
                  {#if field.type === "info"}
                    {#if !field.suppressFromEmails}
                      <div class="info-field-summary">
                        {#if field.icon}
                          <span class="info-icon"
                            ><Icon name={field.icon} size={20} /></span
                          >
                        {/if}
                        <div class="info-content">
                          {#if field.infoTitle}
                            <strong>{field.infoTitle}</strong>
                          {/if}
                          <span class="multiline-value"
                            >{field.infoMessage || ""}</span
                          >
                        </div>
                      </div>
                    {/if}
                  {:else}
                    <p>
                      <strong>{field.label}:</strong>
                      {#if field.type === "checkbox"}
                        {formData[field.name] ? "Yes" : "No"}
                      {:else if field.type === "textarea"}
                        <span class="multiline-value"
                          >{formData[field.name] || "—"}</span
                        >
                      {:else}
                        {formData[field.name] || "—"}
                      {/if}
                    </p>
                  {/if}
                {/each}
              </div>
            </div>

            <!-- Files Uploaded -->
            <div class="summary-section">
              <h3 class="section-title">
                Files Uploaded ({selectedFiles.length})
              </h3>
              <div class="section-content">
                {#if selectedFiles.length > 0}
                  <div class="files-list">
                    {#each selectedFiles as file}
                      <div class="file-item">
                        <span class="file-icon">📄</span>
                        <div class="file-details">
                          <p class="system-filename">{file.name}</p>
                          <span class="file-size"
                            >{formatFileSize(file.size)}</span
                          >
                        </div>
                      </div>
                    {/each}
                  </div>
                {:else}
                  <p class="no-files">No files uploaded</p>
                {/if}
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="summary-actions">
            <Button variant="primary" fullWidth onclick={downloadPDF}>
              Download PDF Summary
            </Button>
          </div>
        </div>
      {/if}
    </Card>
  </div>
</div>

<style>
  .wizard-page {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    min-height: 100vh;
    min-height: 100dvh;
    padding-bottom: 100px; /* Extra space for FABs */
    background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #3d5a7f 100%);
  }

  /* Header - sticky on mobile, with proper light text for dark background */
  .wizard-header {
    color: white;
    padding: var(--space-2) var(--space-4);
    padding-bottom: var(--space-2);
    position: sticky;
    top: 0;
    z-index: 100;
    background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .wizard-header::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 30% 70%,
      rgba(255, 255, 255, 0.08) 0%,
      transparent 50%
    );
    pointer-events: none;
  }

  .header-container {
    max-width: 600px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .header-steps {
    max-width: 600px;
    margin: var(--space-1) auto 0;
    padding: 0 var(--space-4);
    position: relative;
    z-index: 1;
  }

  .header-branding {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-3);
  }

  /* Logo without container - bare image */
  .header-logo-bare {
    display: block;
    object-fit: contain;
    flex-shrink: 0;
  }

  /* Logo with container */
  .header-logo-container {
    flex-shrink: 0;
    border-radius: var(--radius-md);
    padding: var(--space-1);
    line-height: 0;
  }

  .header-logo-container.container-card {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .header-logo-container.container-border {
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .header-logo-container .header-logo {
    display: block;
    object-fit: contain;
  }

  /* Legacy wrapper (fallback) */
  .header-logo-wrapper {
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);
    border-radius: var(--radius-md);
    padding: var(--space-1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .header-logo {
    object-fit: contain;
    display: block;
  }

  .header-text {
    text-align: left;
  }

  /* When no logo, center the text */
  .header-branding:not(:has(.header-logo-wrapper)):not(
      :has(.header-logo-container)
    ):not(:has(.header-logo-bare))
    .header-text {
    text-align: center;
  }

  .wizard-header h1 {
    font-size: var(--text-base);
    font-weight: var(--font-bold);
    margin: 0;
    color: white;
    letter-spacing: -0.02em;
  }

  .wizard-header p {
    font-size: var(--text-xs);
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
    font-weight: var(--font-medium);
  }

  @media (min-width: 640px) {
    .wizard-header {
      padding: var(--space-6) var(--space-6) var(--space-4);
      position: relative;
      background: transparent;
      box-shadow: none;
    }

    .header-branding {
      gap: var(--space-6);
    }

    .header-logo-wrapper {
      padding: var(--space-4);
      border-radius: var(--radius-lg);
    }

    .header-logo {
      width: 64px;
      height: 64px;
    }

    .wizard-header h1 {
      font-size: var(--text-3xl);
      margin-bottom: var(--space-1);
    }

    .wizard-header p {
      font-size: var(--text-lg);
    }
  }

  /* Content */
  .wizard-content {
    flex: 1;
    padding: var(--space-4);
    max-width: 600px;
    margin: 0 auto;
    width: 100%;
  }

  @media (min-width: 640px) {
    .wizard-content {
      padding: var(--space-6);
    }
  }

  .step-content {
    display: flex;
    flex-direction: column;
  }

  .step-title {
    font-size: var(--text-xl);
    font-weight: var(--font-semibold);
    margin: 0 0 var(--space-2);
    color: var(--color-gray-800);
  }

  .step-desc {
    font-size: var(--text-sm);
    color: var(--color-gray-500);
    margin: 0 0 var(--space-6);
  }

  .form-stack {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .divider {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    color: var(--color-gray-400);
    font-size: var(--text-sm);
  }

  .divider::before,
  .divider::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--color-gray-200);
  }

  .email-input-row {
    display: flex;
    align-items: flex-end;
    gap: var(--space-2);
  }

  .email-input-wrapper {
    flex: 1;
    min-width: 0;
  }

  .start-over-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border: 1px solid var(--color-gray-300);
    border-radius: var(--radius-md);
    background: var(--color-white);
    color: var(--color-gray-600);
    font-size: var(--text-xl);
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;
  }

  .start-over-btn:hover {
    background: var(--color-gray-100);
    color: var(--color-gray-800);
    border-color: var(--color-gray-400);
  }

  .start-over-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .resend-link {
    background: none;
    border: none;
    color: var(--color-primary-600);
    font-size: var(--text-sm);
    cursor: pointer;
    text-decoration: underline;
    padding: var(--space-2);
    text-align: center;
    width: 100%;
  }

  .resend-link:hover {
    color: var(--color-primary-700);
  }

  .resend-link:disabled {
    color: var(--color-gray-400);
    cursor: not-allowed;
  }

  .trust-browser-option {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--color-gray-50);
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-md);
  }

  .trust-browser-label {
    flex: 1;
    font-size: var(--text-sm);
    color: var(--color-gray-700);
    line-height: 1.4;
    cursor: pointer;
  }

  .trust-browser-hint {
    display: block;
    color: var(--color-gray-500);
    font-size: var(--text-xs);
    margin-top: 2px;
  }

  .submit-section {
    margin-top: var(--space-6);
  }

  /* Summary Header */
  .summary-header {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
  }

  .summary-check {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--color-success-500);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .check-icon {
    color: white;
    font-size: var(--text-lg);
    font-weight: var(--font-bold);
  }

  /* Printable Summary */
  .print-summary {
    background: white;
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-lg);
    padding: var(--space-5);
    margin-bottom: var(--space-4);
  }

  .summary-branding {
    text-align: center;
    padding-bottom: var(--space-4);
    border-bottom: 1px solid var(--color-gray-200);
    margin-bottom: var(--space-4);
  }

  .org-name {
    font-size: var(--text-lg);
    font-weight: var(--font-bold);
    color: var(--color-gray-800);
    margin: 0 0 var(--space-2);
  }

  .org-contact {
    font-size: var(--text-xs);
    color: var(--color-gray-500);
    margin: 0;
  }

  .summary-title-section {
    text-align: center;
    margin-bottom: var(--space-5);
  }

  .summary-main-title {
    font-size: var(--text-xl);
    font-weight: var(--font-bold);
    color: var(--color-gray-800);
    margin: 0 0 var(--space-1);
  }

  .summary-date {
    font-size: var(--text-sm);
    color: var(--color-gray-500);
    margin: 0;
  }

  .submission-id {
    font-size: var(--text-sm);
    color: var(--color-primary-600);
    margin: var(--space-1) 0 0;
    font-weight: var(--font-medium);
  }

  /* Summary Sections */
  .summary-section {
    margin-bottom: var(--space-4);
  }

  .summary-section:last-child {
    margin-bottom: 0;
  }

  .section-title {
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-primary-700);
    margin: 0 0 var(--space-2);
    padding-bottom: var(--space-2);
    border-bottom: 2px solid var(--color-primary-100);
  }

  .section-content {
    padding-left: var(--space-2);
  }

  .section-content p {
    font-size: var(--text-sm);
    color: var(--color-gray-700);
    margin: 0 0 var(--space-1);
    line-height: 1.6;
  }

  .section-content p:last-child {
    margin-bottom: 0;
  }

  .section-content strong {
    color: var(--color-gray-800);
  }

  /* Multiline field values (textarea) */
  .multiline-value {
    white-space: pre-line;
    display: block;
    margin-top: var(--space-1);
    padding-left: var(--space-2);
  }

  /* Info field summary */
  .info-field-summary {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
    padding: var(--space-3);
    background: var(--color-blue-50);
    border-left: 3px solid var(--color-blue-500);
    border-radius: var(--radius-md);
    margin-bottom: var(--space-3);
  }

  .info-icon {
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
  }

  .info-content {
    flex: 1;
  }

  .info-content strong {
    display: block;
    margin-bottom: var(--space-1);
  }

  /* Field icons */
  :global(.field-icon) {
    display: inline-block;
    vertical-align: middle;
    margin-right: 6px;
    color: var(--color-gray-600);
  }

  /* Files List */
  .files-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .file-item {
    display: flex;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--color-gray-50);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-gray-100);
  }

  .file-icon {
    font-size: var(--text-xl);
    flex-shrink: 0;
  }

  .file-details {
    flex: 1;
    min-width: 0;
  }

  .system-filename {
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-gray-800);
    margin: 0 0 var(--space-1);
    word-break: break-all;
  }

  .file-size {
    display: block;
    font-size: var(--text-xs);
    color: var(--color-gray-400);
    margin-top: var(--space-1);
  }

  .no-files {
    font-size: var(--text-sm);
    color: var(--color-gray-400);
    margin: 0;
    font-style: italic;
  }

  /* Summary Actions */
  .summary-actions {
    margin-top: var(--space-2);
  }

  /* Confirmation Dialog Styles */
  .dialog-intro {
    font-size: var(--text-base);
    color: var(--color-gray-600);
    margin: 0 0 var(--space-5);
    line-height: 1.5;
  }

  .confirm-fields {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    margin-bottom: var(--space-5);
  }

  .confirm-field {
    padding: var(--space-3) var(--space-4);
    background: var(--color-gray-50);
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-gray-200);
  }

  .confirm-field-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-2);
  }

  .confirm-field-label {
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-gray-700);
    margin: 0;
  }

  .confirm-checkbox-label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    cursor: pointer;
    font-size: var(--text-sm);
    color: var(--color-gray-600);
  }

  .confirm-checkbox {
    width: 18px;
    height: 18px;
    accent-color: var(--color-primary-600);
    cursor: pointer;
  }

  .confirm-checkbox-text {
    font-weight: var(--font-medium);
  }

  .confirm-field-value {
    font-size: var(--text-base);
    color: var(--color-gray-800);
    margin: 0;
    font-weight: var(--font-medium);
  }

  .dialog-help {
    font-size: var(--text-sm);
    color: var(--color-gray-500);
    margin: 0;
    padding: var(--space-3);
    background: var(--color-primary-50);
    border-radius: var(--radius-md);
    line-height: 1.5;
  }
</style>
